import { AllEntitiesResolved, Entity, mapAssetClass } from '@api';
import {
  type AppTableFeatures,
  WithNAFallback,
} from '@design/components/table';
import { ColumnDef, Table } from '@tanstack/react-table';
import { RowSelectionCheckbox } from '@terminal/components/row-selection-checkbox/RowSelectionCheckbox';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { TFunction } from 'i18next';
import { CSSProperties } from 'react';
import styles from './TableWatchlistManager.module.scss';
import {
  EntityCell,
  EntityCellBaseHandlers,
} from './table-cells/entity-cell/EntityCell';

export const INPUT_HEADER_COLUMN_ID = 'input';
export const ROW_SELECTED_COLUMN_ID = 'rowSelected';

interface GetColumnsProps {
  t: TFunction;
  optionalColumns: { inputColumn: boolean };
  toggleAllRowsSelection: (checked: boolean) => void;
  handlers: EntityCellBaseHandlers;
}

export const getColumns = ({
  t,
  optionalColumns,
  toggleAllRowsSelection,
  handlers,
}: GetColumnsProps) => {
  const inputColumn: ColumnDef<AppTableFeatures, AllEntitiesResolved> = {
    id: INPUT_HEADER_COLUMN_ID,
    header: t('watchlistManager:inputColumnLabel'),
    cell: props => {
      const { input, tag } = props.row.original;
      const parsedInput = input || '-';

      const inputLabel = (
        <span
          className={styles.text}
          style={
            {
              '--text-color': tag
                ? 'var(--text-default)'
                : 'var(--text-critical)',
            } as CSSProperties
          }
        >
          {parsedInput}
        </span>
      );

      return (
        <TruncateTooltip
          atLength={14}
          text={parsedInput}
          fallbackLabel={inputLabel}
          truncatedLabelWrapper={inputLabel}
        />
      );
    },
  };

  const columns: ColumnDef<AppTableFeatures, AllEntitiesResolved>[] = [
    {
      id: ROW_SELECTED_COLUMN_ID,
      header: ({
        table,
      }: {
        table: Table<AppTableFeatures, AllEntitiesResolved>;
      }) => (
        <RowSelectionCheckbox
          name="watchlist-select"
          testId="watchlist-select-all"
          onChange={(_, e) => toggleAllRowsSelection(e.target.checked)}
          checked={table.getIsAllRowsSelected()}
        />
      ),
      cell: () => <span />,
    },
    ...(optionalColumns.inputColumn ? [inputColumn] : []),
    {
      id: 'entity',
      header: t('watchlistManager:tickerColumnLabel'),
      cell: props => (
        <EntityCell entity={props.row.original} handlers={handlers} />
      ),
    },
    {
      id: 'name',
      header: t('watchlistManager:nameColumnLabel'),
      cell: props => {
        const item = props.row.original;

        return (
          <WithNAFallback
            check={!!item.name}
            value={
              <TruncateTooltip
                atLength={17}
                text={item.name!}
                truncatedLabelWrapper={
                  <span className={styles.text}>{item.name}</span>
                }
              />
            }
          />
        );
      },
    },
    {
      id: 'industry',
      header: t('watchlistManager:industryColumnLabel'),
      cell: props => {
        const item = props.row.original;

        /* i18next-extract-disable-next-line */
        const industryName = t([
          `widget:assetOverview.companyProfile.sectorNames.${item.gics?.industry_group.code}`,
          'widget:assetOverview.companyProfile.sectorNames.notFound',
        ]);

        const check = !!item.gics && !!item.gics.industry_group.code;

        return (
          <WithNAFallback
            check={check}
            value={
              <TruncateTooltip
                atLength={17}
                text={industryName}
                truncatedLabelWrapper={
                  <span className={styles.text}>{industryName}</span>
                }
              />
            }
          />
        );
      },
    },
    {
      id: 'type',
      header: t('watchlistManager:typeColumnLabel'),
      cell: props => {
        const item = props.row.original as Entity;

        const { sub_class } = mapAssetClass(item);
        /* i18next-extract-disable-next-line  */
        const type = t(`analyze:assetSubClass.${sub_class}`);
        return (
          <WithNAFallback
            check={(!!item.sub_class || !!item.asset_class) && !!sub_class}
            value={
              <TruncateTooltip
                atLength={17}
                text={type}
                truncatedLabelWrapper={
                  <span className={styles.text}>{type}</span>
                }
              />
            }
          />
        );
      },
    },
  ];

  return columns;
};
